// HAVI ÁTTEKINTÉS — a partner számlázása hónaponként: TÉNY (számlák tételesen, a piszkozat is) + TERV (még nem
// számlázott, esedékes tételek, a SZÁMLÁZÁS hónapjára — amikor fizet). Az alap (tény / terv / vegyes) jelvénnyel
// kimondva, a hónap nettó összege devizánként (a becsült tételt „becslés" jelöli).
// A fiók-scope MINDEN kérésben ott van (a sidebar váltójából); váltásnál új kérés indul (a kulcs része).
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { PortalErrorState } from '@/components/portal/portal-error-state';
import { PortalHiddenSharedNotice } from '@/components/portal/portal-hidden-shared-notice';
import { PortalInvoiceCard } from '@/components/portal/portal-invoice-card';
import { PortalMonthNav } from '@/components/portal/portal-month-nav';
import { PortalPlanItemRow } from '@/components/portal/portal-plan-item';
import { DomainBadge } from '@/components/shared/domain-badge';
import { EmptyState } from '@/components/shared/empty-state';
import { formatMoney } from '@/components/shared/money-block';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Text } from '@/components/ui/text';
import { portalKeys } from '@/features/portal/api';
import { usePortal } from '@/features/portal/portal-context';
import { useInvoicePdf } from '@/features/portal/use-invoice-pdf';
import { api } from '@/lib/api';
import { formatMonthLabel } from '@/lib/month';

export function PortalBillingMonthView({
  month,
  onMonthChange,
}: {
  /** Az URL-ből jövő hónap (`YYYY-MM`); üres / a tartományon kívüli → a mostani hónap. */
  month: string;
  onMonthChange: (month: string) => void;
}): JSX.Element {
  const { t, i18n } = useTranslation();
  const { accountId } = usePortal();
  const lang = i18n.language === 'en' ? 'en' : 'hu';
  const pdf = useInvoicePdf();

  const q = useQuery({
    queryKey: portalKeys.billing(lang, accountId),
    queryFn: () =>
      api.portal.billing.query({
        query: { lang, ...(accountId !== null ? { accountId } : {}) },
      }),
  });

  if (q.isLoading) return <Skeleton className="h-64 w-full" />;
  if (q.data?.status === 403) {
    return (
      <EmptyState title={t('portal.noAccess.title')} description={t('portal.noAccess.desc')} />
    );
  }
  if (q.data?.status !== 200) {
    return <PortalErrorState onRetry={() => void q.refetch()} />;
  }

  const { current, months } = q.data.body;
  const selected =
    months.find((m) => m.month === month) ?? months.find((m) => m.month === current) ?? months[0];
  if (selected === undefined) {
    return (
      <EmptyState
        title={t('portal.billing.month.empty.title')}
        description={t('portal.billing.month.empty.desc')}
      />
    );
  }
  const hiddenCount = selected.hiddenShared.invoices + selected.hiddenShared.planItems;
  const isEmpty = selected.invoices.length === 0 && selected.planItems.length === 0;

  return (
    <div className="space-y-5">
      <PortalMonthNav
        months={months}
        selected={selected.month}
        current={current}
        onSelect={onMonthChange}
      />

      <section className="space-y-3" aria-label={formatMonthLabel(selected.month, i18n.language)}>
        <div className="flex flex-wrap items-center gap-2.5">
          <Text variant="title" as="h2">
            {formatMonthLabel(selected.month, i18n.language)}
          </Text>
          {!isEmpty && (
            <DomainBadge
              kind="billingBasis"
              value={selected.basis}
              label={t(`portal.billing.month.basis.${selected.basis}`)}
            />
          )}
        </div>

        <PortalHiddenSharedNotice count={hiddenCount} />

        {isEmpty ? (
          <EmptyState
            title={t('portal.billing.month.empty.title')}
            description={t('portal.billing.month.empty.desc')}
          />
        ) : (
          <>
            <Text variant="secondary" as="p">
              {t(`portal.billing.month.basisHint.${selected.basis}`)}
            </Text>

            {(selected.totals.length > 0 || selected.planItems.length > 0) && (
              <Card className="space-y-1 p-4">
                <Text variant="overline" as="div">
                  {t(
                    hiddenCount > 0
                      ? 'portal.billing.hiddenShared.totalsVisible'
                      : 'portal.billing.month.totalsTitle',
                  )}
                </Text>
                {selected.totals.length === 0 && (
                  // nincs összegezhető tétel (pl. egyetlen használat-alapú terv-tétel) — nem üres doboz
                  <Text variant="secondary" as="p" data-testid="portal-month-totals-empty">
                    {t('portal.billing.plan.usageAmount')}
                  </Text>
                )}
                {selected.totals.map((x) => (
                  <div key={x.currency} className="flex flex-wrap items-center gap-2">
                    <span className="font-mono text-[22px] font-semibold tabular-nums tracking-[-0.03em]">
                      {formatMoney(x.net, x.currency, i18n.language)}
                    </span>
                    {x.estimated && (
                      <DomainBadge
                        kind="portalBilling"
                        value="estimate"
                        label={t('portal.billing.plan.estimate')}
                      />
                    )}
                  </div>
                ))}
              </Card>
            )}

            {selected.invoices.length > 0 && (
              <div className="space-y-2.5">
                <Text variant="overline" as="h3">
                  {t('portal.billing.month.invoicesTitle')}
                </Text>
                <ul className="space-y-2.5">
                  {selected.invoices.map((inv) => (
                    <PortalInvoiceCard
                      key={inv.id}
                      invoice={inv}
                      downloading={pdf.downloading === inv.id}
                      onDownload={(id) => void pdf.download(id)}
                    />
                  ))}
                </ul>
              </div>
            )}

            {selected.planItems.length > 0 && (
              <div className="space-y-2.5">
                <Text variant="overline" as="h3">
                  {t('portal.billing.month.planTitle')}
                </Text>
                <Text variant="secondary" as="p">
                  {t('portal.billing.month.planHint')}
                </Text>
                <ul className="space-y-2.5">
                  {selected.planItems.map((it, i) => (
                    <PortalPlanItemRow key={`${it.assignmentId}-${i}`} item={it} />
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
      </section>
    </div>
  );
}
